import React, { useMemo, useState, useEffect } from 'react';
import { SC_COUNTIES, CountyPathConfig } from './scPaths';
import { useWBL } from '../context/WBLContext';
import { UserRole } from '../types';
import { motion, AnimatePresence } from 'motion/react';
import { VerifiedAddressInput } from './Common/VerifiedAddressInput';
import { 
  MapPin, 
  GraduationCap, 
  Briefcase, 
  Handshake, 
  Users,
  LogIn, 
  ArrowLeft, 
  Loader2, 
  Sparkles, 
  AlertCircle, 
  CheckCircle2, 
  BookOpen, 
  Activity,
  Eye,
  EyeOff,
  Info,
  Building2,
  Lock
} from 'lucide-react';

interface SCMapProps {
  onSelectCounty: (countyId: string) => void;
  onSignUpRequest?: (role: UserRole) => void;
  onLoginRequest?: () => void;
}

import { isTraditionalSchool, isCareerTechCenter, resolveDistrictForSchool } from '../utils/schoolUtils';

// Local mock school lists matching each active county to provide rich localized onboarding assistance
const LOCAL_SCHOOLS_MAP: Record<string, string[]> = {
  training: [
    'Training High School (training)',
    'Bayside High School (training)',
    'Valley View High School (training)'
  ],
  spartanburg: [
    'Chapman High School (spartanburg)',
    'Boiling Springs High School (spartanburg)',
    'Landrum High School (spartanburg)',
    'Dorman High School (spartanburg)',
    'Spartanburg High School (spartanburg)',
    'Byrnes High School (spartanburg)',
    'Daniel Morgan Technology Center (spartanburg)',
    'Swofford Career Center (spartanburg)',
    'R.D. Anderson Applied Technology Center (spartanburg)'
  ],
  charleston: [
    'Academic Magnet High School (charleston)',
    'Wando High School (charleston)',
    'West Ashley High School (charleston)',
    'Charleston Charter School for Math and Science (charleston)'
  ],
  greenville: [
    'Greenville High School (greenville)',
    'Riverside High School (greenville)',
    'Eastside High School (greenville)',
    'Wade Hampton High School (greenville)',
    'J.L. Mann High School (greenville)'
  ],
  richland: [
    'Columbia High School (richland)',
    'Spring Valley High School (richland)',
    'Ridge View High School (richland)',
    'Dreher High School (richland)'
  ],
  horry: [
    'Myrtle Beach High School (horry)',
    'Carolina Forest High School (horry)',
    'Socastee High School (horry)',
    'Conway High School (horry)'
  ],
  lexington: [
    'Lexington High School (lexington)',
    'River Bluff High School (lexington)',
    'Airport High School (lexington)',
    'Brookland-Cayce High School (lexington)'
  ],
  beaufort: [
    'Beaufort High School (beaufort)',
    'Battery Creek High School (beaufort)',
    'Bluffton High School (beaufort)',
    'Hilton Head Island High School (beaufort)',
    'May River High School (beaufort)',
    'Whale Branch Early College High School (beaufort)'
  ],
  union: [
    'Union County High School (union)',
    'Union County CATE Center (union)'
  ]
};

export const SCMap: React.FC<SCMapProps> = ({ onSelectCounty, onSignUpRequest, onLoginRequest }) => {
  const { 
    changeTenant, 
    opportunities, 
    allOpportunities,
    tenants, 
    schools,
    allSchools,
    districts,
    allDistricts,
    signInWithEmail, 
    signUpWithRoleAndData,
    verifyStudentTicket,
    activeUser,
    activeTenant,
    systemConfig,
    setCurrentView,
    globalBranding,
    submitRegionInquiry
  } = useWBL();

  const [hoveredCounty, setHoveredCounty] = useState<CountyPathConfig | null>(null);
  const [selectedCountyId, setSelectedCountyId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const urlRegion = params.get('primaryRegion') || params.get('region');
      if (urlRegion && SC_COUNTIES.some(c => c.id === urlRegion.toLowerCase())) {
        return urlRegion.toLowerCase();
      }
      const pathSeg = window.location.pathname.split('/').filter(Boolean)[0];
      if (pathSeg && SC_COUNTIES.some(c => c.id === pathSeg.toLowerCase())) {
        return pathSeg.toLowerCase();
      }
    }
    if (activeTenant && SC_COUNTIES.some(c => c.id === activeTenant.id)) {
      return activeTenant.id;
    }
    return null;
  });
  const [selectedInactiveCounty, setSelectedInactiveCounty] = useState<CountyPathConfig | null>(null);

  // Inactive Region Inquiry Form State
  const [inquiryName, setInquiryName] = useState('');
  const [inquiryEmail, setInquiryEmail] = useState('');
  const [inquiryOrg, setInquiryOrg] = useState('');
  const [isInquirySubmitting, setIsInquirySubmitting] = useState(false);
  const [inquirySuccess, setInquirySuccess] = useState(false);
  const [inquiryError, setInquiryError] = useState<string | null>(null);

  // Organization Team Join Context (from URL)
  const [orgJoinContext, setOrgJoinContext] = useState<{
    isJoinOrg: boolean;
    orgId: string;
    orgName: string;
    address: string;
    extendedRegions: string[];
  } | null>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const isJoinOrgParam = params.get('joinOrg') === '1' || params.get('orgInvite') === 'true' || params.get('role') === 'organization';
      if (isJoinOrgParam) {
        const extRaw = params.get('extendedRegions') || '';
        return {
          isJoinOrg: true,
          orgId: params.get('orgId') || '',
          orgName: params.get('orgName') || '',
          address: params.get('address') || '',
          extendedRegions: extRaw ? extRaw.split(',').map(r => r.trim()).filter(Boolean) : []
        };
      }
    }
    return null;
  });

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const urlRegion = params.get('primaryRegion') || params.get('region');
      if (urlRegion && SC_COUNTIES.some(c => c.id === urlRegion.toLowerCase())) {
        setSelectedCountyId(urlRegion.toLowerCase());
        return;
      }
    }
    if (activeTenant && SC_COUNTIES.some(c => c.id === activeTenant.id)) {
      setSelectedCountyId(activeTenant.id);
    } else {
      setSelectedCountyId(null);
    }
  }, [activeTenant]);

  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Regional Gateway Interactive Form States
  const [authMode, setAuthMode] = useState<'onboard' | 'signin'>('onboard');
  const [selectedRole, setSelectedRole] = useState<'student' | 'organization' | 'advisor' | 'parent' | null>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('joinOrg') === '1' || params.get('orgInvite') === 'true') {
        // Keep hero role unselected so hero sign-up is not prefilled; popout drawer handles the invite
        return null;
      }
      if (params.get('role') === 'parent') return 'parent';
      if (params.get('role') === 'student') return 'student';
      if (params.get('role') === 'advisor') return 'advisor';
    }
    return null;
  });

  useEffect(() => {
    setConfirmPassword('');
  }, [selectedRole]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [signUpStage, setSignUpStage] = useState<1 | 2 | 3>(1);
  const [provideParentEmailMap, setProvideParentEmailMap] = useState(false);

  // Form Fields - Student
  const [studentFirst, setStudentFirst] = useState('');
  const [studentLast, setStudentLast] = useState('');
  const [studentYear, setStudentYear] = useState('2026');
  const [studentDob, setStudentDob] = useState('');
  const [studentParentEmail, setStudentParentEmail] = useState('');
  const [studentSchool, setStudentSchool] = useState('');
  const [studentCtc, setStudentCtc] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [studentPassword, setStudentPassword] = useState('');

  // Form Fields - Employer
  const [employerOrg, setEmployerOrg] = useState('');
  const [employerFirst, setEmployerFirst] = useState('');
  const [employerLast, setEmployerLast] = useState('');
  const [employerAddress, setEmployerAddress] = useState('');
  const [employerEmail, setEmployerEmail] = useState('');
  const [employerPassword, setEmployerPassword] = useState('');

  // Form Fields - Advisor
  const [advisorFirst, setAdvisorFirst] = useState('');
  const [advisorLast, setAdvisorLast] = useState('');
  const [advisorSchool, setAdvisorSchool] = useState('');
  const [advisorEmail, setAdvisorEmail] = useState('');
  const [advisorPassword, setAdvisorPassword] = useState('');

  // Form Fields - Parent
  const [parentFirst, setParentFirst] = useState('');
  const [parentLast, setParentLast] = useState('');
  const [parentStudentName, setParentStudentName] = useState('');
  const [parentStudentSchool, setParentStudentSchool] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentPassword, setParentPassword] = useState('');
  const [parentChildDob, setParentChildDob] = useState('');
  const [parentAccessCode, setParentAccessCode] = useState('');

  // Form Fields - Signin
  const [signinEmail, setSigninEmail] = useState('');
  const [signinPassword, setSigninPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // URL Parameter Handling for Autofill
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const roleParam = params.get('role');
    const isJoinOrgParam = params.get('joinOrg') === '1' || params.get('orgInvite') === 'true';

    // If joinOrg invite, do not prefill the hero card; the popout drawer handles it.
    if (isJoinOrgParam) {
      return;
    }

    if (roleParam === 'parent') {
      const code = params.get('code');
      const dob = params.get('dob');
      const name = params.get('name');
      const school = params.get('school');
      const region = params.get('region');
      setAuthMode('onboard');
      setSelectedRole('parent');
      setSignUpStage(1);
      
      if (code) setParentAccessCode(code);
      if (dob) setParentChildDob(dob);
      if (name) setParentStudentName(decodeURIComponent(name));
      if (school) setParentStudentSchool(decodeURIComponent(school));
      if (region) {
        setSelectedCountyId(region);
        changeTenant(region);
      }
    }
  }, []);

  // Get bounding box for a specific county
  const getCountyBBox = (countyId: string) => {
    const county = SC_COUNTIES.find(c => c.id === countyId);
    if (!county) return null;

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    county.paths.forEach((pathStr) => {
      const coords = pathStr.match(/-?\d+(\.\d+)?/g);
      if (coords) {
        for (let i = 0; i < coords.length; i += 2) {
          const x = parseFloat(coords[i]);
          const y = parseFloat(coords[i + 1]);
          if (!isNaN(x) && !isNaN(y)) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
    });

    const width = Math.max(maxX - minX, 10);
    const height = Math.max(maxY - minY, 10);
    // Apply 6% safety margin on the borders for a relaxed display
    const padX = width * 0.06;
    const padY = height * 0.06;

    return `${minX - padX} ${minY - padY} ${width + padX * 2} ${height + padY * 2}`;
  };

  const visibleCounties = useMemo(() => {
    return SC_COUNTIES.filter(county => {
      const tenantMatch = tenants.find(t => t.id === county.id);
      return tenantMatch ? (tenantMatch.isVisible !== false) : true;
    });
  }, [tenants]);

  const calculatedViewBox = useMemo(() => {
    if (selectedCountyId) {
      const bbox = getCountyBBox(selectedCountyId);
      if (bbox) return bbox;
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    const bboxCounties = visibleCounties.length > 0 ? visibleCounties : SC_COUNTIES;

    bboxCounties.forEach((county) => {
      county.paths.forEach((pathStr) => {
        const coords = pathStr.match(/-?\d+(\.\d+)?/g);
        if (coords) {
          for (let i = 0; i < coords.length; i += 2) {
            const x = parseFloat(coords[i]);
            const y = parseFloat(coords[i + 1]);
            if (!isNaN(x) && !isNaN(y)) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }
      });
    });

    const width = maxX - minX;
    const height = maxY - minY;
    // Apply 5% safety margin on the borders for a relaxed display
    const padX = width * 0.05;
    const padY = height * 0.05;

    return `${minX - padX} ${minY - padY} ${width + padX * 2} ${height + padY * 2}`;
  }, [selectedCountyId, visibleCounties]);

  // Compute stats per county
  const countyOpportunitiesCount = useMemo(() => {
    const stats: Record<string, number> = {};
    const oppList = (allOpportunities && allOpportunities.length > 0) ? allOpportunities : (opportunities || []);

    oppList.forEach((opp) => {
      // Only count open opportunities
      const isOpen = opp.status ? (opp.status === 'open') : (opp.isOpen !== false);
      if (!isOpen) return;

      const matchedCounties = new Set<string>();

      // 1. Direct tenantId / regionId matching a county ID
      if (opp.tenantId && SC_COUNTIES.some(c => c.id === opp.tenantId)) {
        matchedCounties.add(opp.tenantId);
      }
      if (opp.regionId && SC_COUNTIES.some(c => c.id === opp.regionId)) {
        matchedCounties.add(opp.regionId);
      }

      // 2. Target region IDs array
      if (Array.isArray(opp.targetRegionIds)) {
        opp.targetRegionIds.forEach(rId => {
          if (SC_COUNTIES.some(c => c.id === rId)) {
            matchedCounties.add(rId);
          }
        });
      }

      // 3. Location, address, target schools, target districts
      SC_COUNTIES.forEach((county) => {
        const cId = county.id;
        const cName = county.name.toLowerCase();

        if (opp.location && (opp.location.toLowerCase().includes(cName) || opp.location.toLowerCase().includes(cId))) {
          matchedCounties.add(cId);
        }
        if (opp.address && (opp.address.toLowerCase().includes(cName) || opp.address.toLowerCase().includes(cId))) {
          matchedCounties.add(cId);
        }
        if (Array.isArray(opp.targetSchools) && opp.targetSchools.some(s => s.toLowerCase().includes(`(${cId})`) || s.toLowerCase().includes(cName))) {
          matchedCounties.add(cId);
        }
        if (Array.isArray(opp.targetSchoolDistricts) && opp.targetSchoolDistricts.some(d => d.toLowerCase().includes(cName) || d.toLowerCase().includes(cId))) {
          matchedCounties.add(cId);
        }
      });

      // 4. Fallback: if tenantId matches an org tenant that belongs to a county
      if (matchedCounties.size === 0 && opp.tenantId) {
        const orgTenant = tenants.find(t => t.id === opp.tenantId);
        if (orgTenant && SC_COUNTIES.some(c => c.id === orgTenant.id)) {
          matchedCounties.add(orgTenant.id);
        }
      }

      // Increment stats for all matched counties
      matchedCounties.forEach(countyId => {
        stats[countyId] = (stats[countyId] || 0) + 1;
      });
    });

    return stats;
  }, [allOpportunities, opportunities, tenants]);

  // Find currently active county object
  const activeCountyConfig = useMemo(() => {
    if (!selectedCountyId) return null;
    return SC_COUNTIES.find(c => c.id === selectedCountyId) || null;
  }, [selectedCountyId]);

  // Find currently active county tenant
  const selectedTenant = useMemo(() => {
    if (!selectedCountyId) return null;
    return tenants.find(t => t.id === selectedCountyId) || activeTenant;
  }, [selectedCountyId, tenants, activeTenant]);

  // Handle county interactions
  const handleMouseMove = (e: React.MouseEvent) => {
    const container = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: e.clientX - container.left,
      y: e.clientY - container.top,
    });
  };

  const getCountyCenter = (county: CountyPathConfig) => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    county.paths.forEach((pathStr) => {
      const coords = pathStr.match(/-?\d+(\.\d+)?/g);
      if (coords) {
        for (let i = 0; i < coords.length; i += 2) {
          const x = parseFloat(coords[i]);
          const y = parseFloat(coords[i + 1]);
          if (!isNaN(x) && !isNaN(y)) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
    });
    return `${(minX + maxX) / 2}px ${(minY + maxY) / 2}px`;
  };

  const handleCountyClick = (county: CountyPathConfig) => {
    setSelectedInactiveCounty(null);
    setSelectedCountyId(county.id);
    changeTenant(county.id);
    setSignUpStage(1);
    setSelectedRole(null);
    setAuthError(null);
    setSuccessMsg(null);
  };

  const handleInactiveCountyClick = (county: CountyPathConfig) => {
    setSelectedCountyId(null);
    setSelectedInactiveCounty(county);
    setInquirySuccess(false);
    setInquiryError(null);
    setInquiryName(activeUser?.displayName || '');
    setInquiryEmail(activeUser?.email || '');
    setInquiryOrg(activeUser?.organizationName || '');
  };

  // Reset form helper
  const handleBackToMap = () => {
    setSelectedCountyId(null);
    setSelectedInactiveCounty(null);
    setInquirySuccess(false);
    setInquiryError(null);
    setSignUpStage(1);
    setSelectedRole(null);
    setAuthError(null);
    setSuccessMsg(null);
    changeTenant('global');
  };

  const handleInquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInactiveCounty) return;

    if (!inquiryName.trim() || !inquiryEmail.trim()) {
      setInquiryError('Please provide your name and email address.');
      return;
    }

    setIsInquirySubmitting(true);
    setInquiryError(null);

    try {
      await submitRegionInquiry({
        regionId: selectedInactiveCounty.id,
        regionName: selectedInactiveCounty.name,
        name: inquiryName.trim(),
        email: inquiryEmail.trim(),
        organization: inquiryOrg.trim() || undefined,
      });
      setInquirySuccess(true);
    } catch (err: any) {
      console.error('Failed submitting region inquiry:', err);
      setInquiryError(err.message || 'Failed to submit request. Please try again.');
    } finally {
      setIsInquirySubmitting(false);
    }
  };

  // Handle local Sign-In trigger
  const handleLocalSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setIsSubmitting(true);
    try {
      if (!signinEmail || !signinPassword) {
        throw new Error("Please submit both authorization fields.");
      }
      await signInWithEmail(signinEmail, signinPassword);
      setSuccessMsg("Authorization accepted. Redirecting home...");
      setCurrentView('dashboard');
    } catch (err: any) {
      console.warn("Auth warning:", err.message);
      let errorMessage = "Failed to authorize credentials. Check characters and retry.";
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        errorMessage = "Invalid email or password. Please try again.";
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = "Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later.";
      } else if (err.message) {
        errorMessage = err.message;
      }
      setAuthError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validate Stage transitions
  const handleNextStage = async (e: React.MouseEvent) => {
    e.preventDefault();
    setAuthError(null);
    if (!selectedCountyId) return;

    if (signUpStage === 1) {
      if (selectedRole === 'student') {
        if (!studentFirst.trim() || !studentLast.trim() || !studentDob) {
          setAuthError("Please specify First Name, Last Name, and Birth Date.");
          return;
        }
        
        const birthDate = new Date(studentDob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        
        if (age < 13) {
          setAuthError("We are thrilled you want to join us! However, to comply with privacy policy guidelines, our portal is only available to users who are 13 years of age or older. We apologize for any inconvenience.");
          return;
        }
        setSignUpStage(2);
      } else if (selectedRole === 'organization') {
        if (!employerOrg.trim() || !employerFirst.trim() || !employerLast.trim() || !employerAddress.trim()) {
          setAuthError("Please specify Enterprise Name, Representative Names, and Headquarters Address.");
          return;
        }
        setSignUpStage(2);
      } else if (selectedRole === 'advisor') {
        if (!advisorFirst.trim() || !advisorLast.trim()) {
          setAuthError("Please specify First Name and Last Name.");
          return;
        }
        setSignUpStage(2);
      } else if (selectedRole === 'parent') {
        if (!parentFirst.trim() || !parentLast.trim() || !parentStudentName.trim() || !parentChildDob || !parentAccessCode.trim()) {
          setAuthError("Please specify all fields including Child's DOB and Access Code.");
          return;
        }

        // Verify the parent's ticket (Child DOB + Access Code) before continuing
        const studentId = await verifyStudentTicket(parentChildDob, parentAccessCode);
        if (!studentId) {
          setAuthError("Verification failed: The provided Child's DOB or Access Code does not match any registered student in our system. Please check the email sent to you.");
          return;
        }

        setSignUpStage(2);
      }
    } else if (signUpStage === 2) {
      if (selectedRole === 'student') {
        if (!studentSchool) {
          setAuthError("Please select your Education Institution.");
          return;
        }
        if (!studentEmail.trim()) {
          setAuthError("Please specify your Student Email.");
          return;
        }
        if (!studentPassword) {
          setAuthError("Please provide a password.");
          return;
        }
        if (studentPassword !== confirmPassword) {
          setAuthError("Passwords do not match.");
          return;
        }
        
        if (!hasMinLength || !hasCapital || !hasNumber || !hasSymbol) {
          setAuthError("Password does not meet all complexity requirements listed below.");
          return;
        }
        
        setSignUpStage(3);
      }
    }
  };

  const getCountySchoolList = (countyId: string | null): string[] => {
    if (!countyId) return [];
    const normalized = countyId.toLowerCase();
    const fromAllSchools = (allSchools || [])
      .filter(s => s && s.regionId && s.regionId.toLowerCase() === normalized)
      .map(s => s.name);
    const fromLocal = LOCAL_SCHOOLS_MAP[normalized] || [];
    const combined = Array.from(new Set([...fromAllSchools, ...fromLocal])).filter(Boolean);
    return combined;
  };

  // Handle local Onboarding / Signup trigger
  const handleLocalOnboard = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setIsSubmitting(true);
    try {
      if (!selectedCountyId) return;

      if (selectedRole === 'student') {
        const birthDate = new Date(studentDob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        const isUnder18 = age < 18;
        if (!studentFirst.trim() || !studentLast.trim() || !studentSchool || !studentEmail || !studentPassword || (isUnder18 && !studentParentEmail.trim())) {
          throw new Error("Please complete all student credential parameters.");
        }
        if (!isUnder18 && provideParentEmailMap && !studentParentEmail.trim()) {
          throw new Error("Please provide a valid parent email address or choose 'No, Skip'.");
        }
        if (studentPassword !== confirmPassword) throw new Error("Passwords do not match.");
        
        if (age < 13) {
          throw new Error("We are thrilled you want to join us! However, to comply with privacy policy guidelines, our portal is only available to users who are 13 years of age or older. We apologize for any inconvenience.");
        }
        
        const resolved = resolveDistrictForSchool(studentSchool, selectedCountyId, allSchools, allDistricts);
        const resolvedRegion = resolved.regionId || selectedCountyId;
        const data = {
          contactFirstName: studentFirst,
          contactLastName: studentLast,
          dateOfBirth: studentDob || '2008-01-01',
          classYear: studentYear,
          school: resolved.schoolName || studentSchool,
          schoolId: resolved.schoolId,
          additionalSchools: studentCtc ? [studentCtc] : [],
          parentEmail: (isUnder18 || provideParentEmailMap) ? studentParentEmail : '',
          district: resolved.districtName,
          districtId: resolved.districtId,
          academicLevel: 'High School',
          tenantId: resolvedRegion
        };

        await signUpWithRoleAndData(studentEmail, studentPassword, 'student', data);
      } else if (selectedRole === 'organization') {
        if (!employerOrg.trim() || !employerFirst.trim() || !employerLast.trim() || !employerAddress.trim() || !employerEmail || !employerPassword) {
          throw new Error("Please complete all employer onboarding constraints.");
        }
        if (employerPassword !== confirmPassword) throw new Error("Passwords do not match.");

        const isJoiningTeam = !!(orgJoinContext?.isJoinOrg || orgJoinContext?.orgId);
        const data = {
          orgName: employerOrg,
          contactFirstName: employerFirst,
          contactLastName: employerLast,
          address: employerAddress,
          profileComplete: true,
          tenantId: selectedCountyId,
          ...(isJoiningTeam ? {
            isJoinOrg: true,
            orgId: orgJoinContext?.orgId || '',
            parentOrgId: orgJoinContext?.orgId || '',
            orgTeamId: orgJoinContext?.orgId || '',
            extendedRegions: orgJoinContext?.extendedRegions || []
          } : {})
        };

        await signUpWithRoleAndData(employerEmail, employerPassword, 'organization', data);
      } else if (selectedRole === 'advisor') {
        if (!advisorFirst.trim() || !advisorLast.trim() || !advisorSchool || !advisorEmail || !advisorPassword) {
          throw new Error("Please complete all counselor validation requirements.");
        }
        if (advisorPassword !== confirmPassword) throw new Error("Passwords do not match.");

        const resolved = resolveDistrictForSchool(advisorSchool, selectedCountyId, allSchools, allDistricts);
        const resolvedRegion = resolved.regionId || selectedCountyId;
        const data = {
          contactFirstName: advisorFirst,
          contactLastName: advisorLast,
          signupSchool: resolved.schoolName || advisorSchool,
          signupSchoolId: resolved.schoolId || '',
          school: resolved.schoolName || advisorSchool,
          schoolId: resolved.schoolId || '',
          pendingSchool: resolved.schoolName || advisorSchool,
          pendingSchoolId: resolved.schoolId || '',
          district: resolved.districtName,
          districtId: resolved.districtId,
          tenantId: resolvedRegion
        };

        await signUpWithRoleAndData(advisorEmail, advisorPassword, 'advisor', data);
      } else if (selectedRole === 'parent') {
        const missingFields = [];
        if (!parentFirst.trim()) missingFields.push("First Name");
        if (!parentLast.trim()) missingFields.push("Last Name");
        if (!parentStudentName.trim()) missingFields.push("Student Name");
        if (!parentStudentSchool) missingFields.push("School Selection");
        if (!parentEmail.trim()) missingFields.push("Parent Email");
        if (!parentPassword) missingFields.push("Password");
        if (!parentChildDob) missingFields.push("Child's Date of Birth");
        if (!parentAccessCode.trim()) missingFields.push("Access Code");

        if (missingFields.length > 0) {
          throw new Error(`Please complete: ${missingFields.join(', ')}`);
        }
        if (parentPassword !== confirmPassword) throw new Error("Passwords do not match.");

        // Verify the parent's ticket (Child DOB + Access Code)
        const studentId = await verifyStudentTicket(parentChildDob, parentAccessCode);
        if (!studentId) {
          throw new Error("Verification failed: The provided Child's DOB or Access Code does not match any registered student in our system. Please check the email sent to you.");
        }

        const resolved = resolveDistrictForSchool(parentStudentSchool, selectedCountyId, allSchools, allDistricts);
        const resolvedRegion = resolved.regionId || selectedCountyId;
        const data = {
          contactFirstName: parentFirst,
          contactLastName: parentLast,
          studentChildName: parentStudentName,
          childDob: parentChildDob,
          accessCode: parentAccessCode.toUpperCase(),
          childId: studentId,
          school: resolved.schoolName || parentStudentSchool,
          schoolId: resolved.schoolId,
          district: resolved.districtName,
          districtId: resolved.districtId,
          tenantId: resolvedRegion
        };

        await signUpWithRoleAndData(parentEmail, parentPassword, 'parent', data);
      }

      setSuccessMsg("Onboarding finalized successfully! Redirecting to customized workspace...");
    } catch (err: any) {
      console.warn("Auth warning:", err.message);
      let errorMessage = "Could not register details. Correct the fields and submit again.";
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = "This email is already registered. Please sign in instead.";
      } else if (err.code === 'auth/weak-password') {
        errorMessage = "Password should be at least 6 characters.";
      } else if (err.message) {
        errorMessage = err.message;
      }
      setAuthError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dynamic values depending on active county / region color scheme (LIGHT MAIN)
  const dynamicBrandingTheme = useMemo(() => {
    const globalLightMain = globalBranding?.palette?.lightMain || '#0094c8';
    if (!selectedCountyId) {
      return { primary: globalLightMain, shadow: 'rgba(0, 148, 200, 0.4)' };
    }

    // 1. Locate the specific region / tenant record
    const targetRegion = tenants.find(
      (t) => t.id.toLowerCase() === selectedCountyId.toLowerCase()
    );

    // 2. Check if the region has an explicit palette with lightMain configured
    const configuredLightMain = targetRegion?.palette?.lightMain;
    if (configuredLightMain && configuredLightMain.trim()) {
      return { 
        primary: configuredLightMain.trim(), 
        shadow: `rgba(0, 148, 200, 0.4)` 
      };
    }

    // 3. Known regional defaults if not yet customized in branding
    const themes: Record<string, { primary: string, shadow: string }> = {
      'spartanburg': { primary: '#0094c8', shadow: 'rgba(0, 148, 200, 0.4)' },
      'charleston': { primary: '#38bdf8', shadow: 'rgba(56, 189, 248, 0.4)' },
      'greenville': { primary: '#10b981', shadow: 'rgba(16, 185, 129, 0.4)' },
      'richland': { primary: '#8b5cf6', shadow: 'rgba(139, 92, 246, 0.4)' },
      'horry': { primary: '#f59e0b', shadow: 'rgba(245, 158, 11, 0.4)' },
      'lexington': { primary: '#ec4899', shadow: 'rgba(236, 72, 153, 0.4)' },
      'training': { primary: '#0ea5e9', shadow: 'rgba(14, 165, 233, 0.4)' },
      'cherokee': { primary: targetRegion?.primaryColor || '#0094c8', shadow: 'rgba(0, 148, 200, 0.4)' },
      'union': { primary: targetRegion?.primaryColor || '#0094c8', shadow: 'rgba(0, 148, 200, 0.4)' },
      'beaufort': { primary: targetRegion?.primaryColor || '#0094c8', shadow: 'rgba(0, 148, 200, 0.4)' }
    };

    if (themes[selectedCountyId.toLowerCase()]) {
      return themes[selectedCountyId.toLowerCase()];
    }

    // 4. Region primaryColor or Global Branding Light Main fallback
    const fallbackColor = targetRegion?.primaryColor || globalLightMain;
    return { primary: fallbackColor, shadow: 'rgba(0, 148, 200, 0.4)' };
  }, [selectedCountyId, tenants, globalBranding]);

  const currentPassword = 
    selectedRole === 'student' ? studentPassword :
    selectedRole === 'organization' ? employerPassword :
    selectedRole === 'advisor' ? advisorPassword :
    selectedRole === 'parent' ? parentPassword : '';

  const hasMinLength = currentPassword.length >= 8;
  const hasCapital = /[A-Z]/.test(currentPassword);
  const hasNumber = /\d/.test(currentPassword);
  const hasSymbol = /[^A-Za-z0-9]/.test(currentPassword);
  const passwordsMatch = currentPassword.length > 0 && currentPassword === confirmPassword;

  return (
    <div id="sc-map-container" className="w-full flex items-start justify-center relative p-1 overflow-visible">
      
      <AnimatePresence mode="wait">
        {/* 1. CLOSED MAP VIEWPORT */}
        {!selectedCountyId && !selectedInactiveCounty ? (
          <motion.div 
            key="map-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-4xl flex flex-col items-center justify-center relative cursor-crosshair"
            onMouseMove={handleMouseMove}
          >

            <svg
              viewBox={calculatedViewBox}
              id="sc_interactive_svg_map"
              className="w-full h-auto max-h-[580px]"
              xmlns="http://www.w3.org/2000/svg"
            >
              {visibleCounties.map((county) => {
                const tenantMatch = tenants.find((t) => t.id === county.id);
                const isPortalActive = tenantMatch ? (tenantMatch.isActive === true) : (county.id === 'spartanburg' || county.id === 'training');
                const isHovered = hoveredCounty?.id === county.id;

                let strokeColor = "rgba(255, 255, 255, 0.45)";
                let strokeWidthValue = "1.0";
                let fillColor = "rgba(255, 255, 255, 0.20)";
                let filterStyle = "none";

                if (county.id === 'training') {
                  strokeColor = isHovered ? "#F9C74F" : "rgba(255, 255, 255, 0.45)";
                  strokeWidthValue = isHovered ? "2.2" : "1.2";
                  fillColor = isHovered ? "rgba(249, 199, 79, 0.3)" : "rgba(255, 255, 255, 0.12)";
                  filterStyle = isHovered ? "drop-shadow(0 2px 12px rgba(249, 199, 79, 0.8))" : "drop-shadow(0 2px 6px rgba(0, 0, 0, 0.4))";
                } else if (isPortalActive) {
                  if (isHovered) {
                    strokeColor = "#F9C74F"; 
                    strokeWidthValue = "2.5";
                    fillColor = "rgba(249, 199, 79, 0.35)"; 
                    filterStyle = "drop-shadow(0 4px 16px rgba(249, 199, 79, 0.85))";
                  } else {
                    strokeColor = "rgba(249, 199, 79, 0.95)"; 
                    strokeWidthValue = "1.6";
                    fillColor = "rgba(249, 199, 79, 0.22)"; 
                    filterStyle = "drop-shadow(0 3px 8px rgba(0, 0, 0, 0.5))";
                  }
                } else {
                  // Inactive Region
                  if (isHovered) {
                    strokeColor = "#38bdf8"; 
                    strokeWidthValue = "2.2";
                    fillColor = "rgba(56, 189, 248, 0.25)"; 
                    filterStyle = "drop-shadow(0 2px 14px rgba(56, 189, 248, 0.8))";
                  } else {
                    strokeColor = "rgba(255, 255, 255, 0.55)";
                    strokeWidthValue = "1.0";
                    fillColor = "rgba(255, 255, 255, 0.18)";
                    filterStyle = "none";
                  }
                }
                
                return (
                  <motion.g 
                    key={county.id}
                    layoutId={`county-g-${county.id}`}
                    onClick={() => isPortalActive ? handleCountyClick(county) : handleInactiveCountyClick(county)}
                    onMouseEnter={() => setHoveredCounty(county)}
                    onMouseLeave={() => setHoveredCounty(null)}
                    className="cursor-pointer group"
                    initial={false}
                    animate={{ opacity: 1, scale: 1 }}
                    style={{ transformOrigin: getCountyCenter(county) }}
                  >
                    {county.paths.map((d, index) => (
                      <motion.path
                        key={`${county.id}-${index}`}
                        layoutId={`county-path-${county.id}-${index}`}
                        d={d}
                        id={`sc-county-path-${county.id}-${index}`}
                        fill={fillColor}
                        stroke={strokeColor}
                        strokeWidth={strokeWidthValue}
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                        className="transition-all duration-300 ease-out"
                        style={{ filter: filterStyle }}
                      />
                    ))}
                    {county.id === 'training' && (
                      <text
                        x="895"
                        y="702"
                        textAnchor="middle"
                        dominantBaseline="central"
                        fill={isHovered ? "#F9C74F" : "rgba(255, 255, 255, 0.75)"}
                        fontSize="8.5"
                        fontWeight="bold"
                        letterSpacing="1"
                        fontFamily="sans-serif"
                        className="pointer-events-none select-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
                      >
                        TRAINING
                      </text>
                    )}
                  </motion.g>
                );
              })}
            </svg>

            {/* Floating cursor tooltip */}
            <AnimatePresence>
              {hoveredCounty && (() => {
                const tenantMatch = tenants.find((t) => t.id === hoveredCounty.id);
                const isPortalActive = tenantMatch ? (tenantMatch.isActive === true) : (hoveredCounty.id === 'spartanburg' || hoveredCounty.id === 'training');
                
                return (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    style={{
                      position: 'absolute',
                      left: tooltipPosition.x + 15,
                      top: tooltipPosition.y + 15,
                      pointerEvents: 'none',
                    }}
                    className="z-50 bg-slate-950/95 text-white border border-white/20 p-3 rounded-xl shadow-2xl backdrop-blur-md flex flex-col text-left space-y-1.5 min-w-[200px]"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-2">
                        <MapPin className={`w-3.5 h-3.5 ${isPortalActive ? 'text-[#F9C74F]' : 'text-sky-400'}`} />
                        <span className="font-bold text-sm tracking-tight">{hoveredCounty.name}</span>
                      </div>
                      {isPortalActive ? (
                        <span className="text-[8.5px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-bold uppercase tracking-wider border border-amber-500/30">
                          Active
                        </span>
                      ) : (
                        <span className="text-[8.5px] px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 font-bold uppercase tracking-wider border border-sky-500/30">
                          Inactive
                        </span>
                      )}
                    </div>
                    {isPortalActive ? (
                      <>
                        <div className="text-[10px] text-slate-300 font-mono pl-5">
                          {countyOpportunitiesCount[hoveredCounty.id] || 0} Open job positions
                        </div>
                        <div className="text-[9.5px] text-[#F9C74F] font-bold pl-5 pt-0.5 flex items-center gap-1">
                          Click to enter portal →
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-[10px] text-slate-400 pl-5 leading-tight">
                          Work-Based Learning portal not active
                        </div>
                        <div className="text-[9.5px] text-sky-400 font-bold pl-5 pt-0.5 flex items-center gap-1">
                          <Info className="w-2.5 h-2.5 shrink-0" /> Click to submit regional inquiry →
                        </div>
                      </>
                    )}
                  </motion.div>
                );
              })()}
            </AnimatePresence>
          </motion.div>
        ) : selectedCountyId ? (

          /* 2. OPENED / EXPANDED INLINE PORTAL HUB (CENTERPIECE + WBL ENTRY) */
          <motion.div 
            key="portal-view"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 180, damping: 25 }}
            className="w-full max-w-[650px] bg-slate-950/85 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl flex flex-col items-stretch overflow-hidden isolate transform-gpu text-white"
          >
            {/* TOP ROW: TWO CELL GRID */}
            <div className="flex flex-col md:flex-row items-stretch border-b border-white/10">
              
              {/* CELL 1: REGION MAP DISPLAY */}
              <div className="w-full md:w-1/2 bg-slate-900/60 p-3.5 md:p-4 flex flex-col justify-between items-center gap-2 relative border-b md:border-b-0 md:border-r border-white/10 rounded-t-2xl md:rounded-tr-none md:rounded-l-2xl md:rounded-bl-2xl">
                {/* Visual tech-line grids for aesthetic modern feel */}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />

                <div className="w-full flex items-center justify-start z-10">
                  {systemConfig?.multiTenantEnabled !== false && (
                    <button 
                      onClick={handleBackToMap}
                      className="text-slate-300 hover:text-white text-[9px] md:text-[10.5px] tracking-wider uppercase font-extrabold flex items-center gap-1.2 bg-white/5 hover:bg-white/10 px-2.5 py-1.5 rounded-md border border-white/10 transition-all shadow-md"
                    >
                      <ArrowLeft className="w-2.5 h-2.5" /> Back to Map
                    </button>
                  )}
                </div>

                {/* Glowing isolated shape display - fills container width, centered and middle justified */}
                <div className="flex-1 w-full flex flex-col items-center justify-center p-3 sm:p-4 relative min-h-[160px] md:min-h-[190px]">
                  <div 
                    className="absolute inset-4 blur-2xl opacity-25 transition-all duration-700 pointer-events-none" 
                    style={{ backgroundColor: dynamicBrandingTheme.primary, boxShadow: `0 0 50px 15px ${dynamicBrandingTheme.primary}` }}
                  />
                  <div className="w-full h-full flex items-center justify-center relative">
                    <svg
                      viewBox={getCountyBBox(selectedCountyId) || "0 0 100 100"}
                      preserveAspectRatio="xMidYMid meet"
                      className="w-full h-full max-h-[145px] sm:max-h-[165px] md:max-h-[180px] relative drop-shadow-[0_0_15px_rgba(255,255,255,0.18)] select-none animate-pulse duration-[4000ms]"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      {activeCountyConfig && (
                        <motion.g layoutId={`county-g-${selectedCountyId}`}>
                          {activeCountyConfig.paths.map((d, index) => (
                            <motion.path
                               key={`${selectedCountyId}-${index}`}
                               layoutId={`county-path-${selectedCountyId}-${index}`}
                               d={d}
                               fill="rgba(255, 255, 255, 0.08)"
                               stroke={dynamicBrandingTheme.primary}
                               strokeWidth="2"
                               strokeLinejoin="round"
                               vectorEffect="non-scaling-stroke"
                               style={{
                                 filter: `drop-shadow(0 0 8px ${dynamicBrandingTheme.primary})`
                               }}
                            />
                          ))}
                        </motion.g>
                      )}
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                      <div className="text-[10px] sm:text-[11px] md:text-[12px] font-black uppercase tracking-widest text-white select-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] bg-slate-950/60 px-3 py-1 rounded-full border border-white/10 backdrop-blur-md">
                        {activeCountyConfig?.name || selectedCountyId}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CELL 2: MEMBER ROLE SELECTOR */}
              <div className="w-full md:w-1/2 p-3.5 md:p-4 flex flex-col justify-between space-y-2.5 rounded-b-2xl md:rounded-b-none md:rounded-br-2xl">
                 {/* Custom Tab selector */}
                <div className="flex border-b border-white/10 text-[10px] gap-1.5">
                  <button
                    onClick={() => { setAuthMode('onboard'); setSignUpStage(1); setSelectedRole(null); setAuthError(null); setSuccessMsg(null); }}
                    className={`py-1 px-2.5 font-black uppercase tracking-wider relative transition-all ${authMode === 'onboard' ? 'text-white' : 'text-slate-400 hover:text-slate-200'}`}
                  >
                    <span>New User</span>
                    {authMode === 'onboard' && (
                      <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ backgroundColor: dynamicBrandingTheme.primary }} />
                    )}
                  </button>
                  <button
                    onClick={() => { 
                      if (onLoginRequest) {
                        onLoginRequest();
                      } else {
                        setAuthMode('signin'); 
                        setSignUpStage(1); 
                        setSelectedRole(null); 
                        setAuthError(null); 
                        setSuccessMsg(null); 
                      }
                    }}
                    className={`py-1 px-2.5 font-black uppercase tracking-wider relative transition-all ${authMode === 'signin' ? 'text-white' : 'text-slate-400 hover:text-slate-200'}`}
                  >
                    <span>Returning User</span>
                    {authMode === 'signin' && (
                      <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ backgroundColor: dynamicBrandingTheme.primary }} />
                    )}
                  </button>
                </div>

                {/* SPECIALIZED ROLE SELECTOR PILLS */}
                <div className="flex-1 flex flex-col justify-center">
                  {authMode === 'onboard' ? (
                    <div className="space-y-1">
                      <label className="text-[9px] font-extrabold uppercase tracking-widest text-slate-400/80 block mb-0.5">Select Member Role</label>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <button
                          type="button"
                          onClick={() => (window as any).openSignupDrawer('student')}
                          className={`py-2 px-2.5 rounded-lg border flex flex-col items-center justify-center gap-1 transition-all text-center cursor-pointer hover:bg-white/5 active:scale-95 ${selectedRole === 'student' ? 'bg-white/10 text-white font-bold' : 'bg-transparent text-slate-400 border-white/5 hover:border-white/10'}`}
                          style={{ borderColor: selectedRole === 'student' ? dynamicBrandingTheme.primary : '' }}
                        >
                          <GraduationCap className="w-3.5 h-3.5" style={{ color: '#F9C74F' }} />
                          <span className="text-[8px] md:text-[9.5px] tracking-wider uppercase font-bold">Student</span>
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => (window as any).openSignupDrawer('organization')}
                          className={`py-2 px-2.5 rounded-lg border flex flex-col items-center justify-center gap-1 transition-all text-center cursor-pointer hover:bg-white/5 active:scale-95 ${selectedRole === 'organization' ? 'bg-white/10 text-white font-bold' : 'bg-transparent text-slate-400 border-[#ffffff05] hover:border-[#ffffff10]'}`}
                          style={{ borderColor: selectedRole === 'organization' ? dynamicBrandingTheme.primary : '' }}
                        >
                          <Briefcase className="w-3.5 h-3.5 text-emerald-300" />
                          <span className="text-[8px] md:text-[9.5px] tracking-wider uppercase font-bold">Employer</span>
                        </button>
 
                         <button
                           type="button"
                           onClick={() => { 
                             if (typeof (window as any).smoothNavigate === 'function') {
                               (window as any).smoothNavigate('/parent-onboarding');
                             } else {
                               window.location.pathname = '/parent-onboarding';
                             }
                           }}
                           className={`py-2 px-2.5 rounded-lg border flex flex-col items-center justify-center gap-1 transition-all text-center cursor-pointer hover:bg-white/5 active:scale-95 ${selectedRole === 'parent' ? 'bg-white/10 text-white font-bold' : 'bg-transparent text-slate-400 border-[#ffffff05] hover:border-[#ffffff10]'}`}
                           style={{ borderColor: selectedRole === 'parent' ? dynamicBrandingTheme.primary : '' }}
                         >
                           <Users className="w-3.5 h-3.5 text-pink-300" />
                           <span className="text-[8px] md:text-[9.5px] tracking-wider uppercase font-bold">Parent</span>
                         </button>
                        
                        <button
                          type="button"
                          onClick={() => (window as any).openSignupDrawer('advisor')}
                          className={`py-2 px-2.5 rounded-lg border flex flex-col items-center justify-center gap-1 transition-all text-center cursor-pointer hover:bg-white/5 active:scale-95 ${selectedRole === 'advisor' ? 'bg-white/10 text-white font-bold' : 'bg-transparent text-slate-400 border-white/5 hover:border-white/10'}`}
                          style={{ borderColor: selectedRole === 'advisor' ? dynamicBrandingTheme.primary : '' }}
                        >
                          <Handshake className="w-3.5 h-3.5 text-sky-300" />
                          <span className="text-[8px] md:text-[9.5px] tracking-wider uppercase font-bold">Advisor</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-[11px] md:text-xs text-slate-400 leading-relaxed py-1">
                      Access established local workspace portals for South Carolina school hubs, students, and employers. Enter registered credentials below to sign in.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* BOTTOM ROW: MERGED INPUT FORM CELL */}
            <AnimatePresence>
              {!(authMode === 'onboard' && selectedRole === null) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full bg-slate-900/10 border-t border-white/5 overflow-hidden rounded-b-2xl"
                >
                  <div className="p-4 md:p-4.5">
              {/* Error & Success indicators */}
              {authError && (
                <div className="p-2.5 mb-3 bg-red-950/50 border border-red-500/20 text-red-300 text-xs rounded-xl flex items-start gap-2 shadow-sm animate-shake">
                  <AlertCircle className="w-4 h-4 shrink-0 text-red-400 mt-0.5" />
                  <p className="font-medium leading-relaxed">{authError}</p>
                </div>
              )}
              {successMsg && (
                <div className="p-2.5 mb-3 bg-emerald-950/50 border border-emerald-500/20 text-emerald-300 text-xs rounded-xl flex items-start gap-2 shadow-sm animate-fade-in">
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400 mt-0.5" />
                  <p className="font-semibold leading-relaxed">{successMsg}</p>
                </div>
              )}

              {/* FORM FIELDS MULTIPLEXER */}
              {authMode === 'onboard' ? (
                <form onSubmit={handleLocalOnboard} className="space-y-3">
                  
                  {/* Stage Progress Indicator */}
                  <div className="flex items-center gap-2 mb-1.5 pb-1 border-b border-white/5 justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[9px] uppercase tracking-wider font-extrabold ${signUpStage === 1 ? 'text-[#F9C74F]' : 'text-slate-500'}`}>
                        1. Profile
                      </span>
                      <span className="text-slate-700 text-[9px] font-bold">&rarr;</span>
                      <span className={`text-[9px] uppercase tracking-wider font-extrabold ${signUpStage === 2 ? 'text-[#F9C74F]' : 'text-slate-500'}`}>
                        2. Credentials
                      </span>
                      {selectedRole === 'student' && (
                        <>
                          <span className="text-slate-700 text-[9px] font-bold">&rarr;</span>
                          <span className={`text-[9px] uppercase tracking-wider font-extrabold ${signUpStage === 3 ? 'text-[#F9C74F]' : 'text-slate-500'}`}>
                            3. Parent Contact
                          </span>
                        </>
                      )}
                    </div>
                    <span className="text-[9px] font-mono text-slate-500 font-bold uppercase">
                      Role: {selectedRole}
                    </span>
                  </div>

                  <div className="space-y-2.5 pt-0.5">
                    {/* STAGE 1: Profile Details */}
                    {signUpStage === 1 && (
                      <div className="space-y-2.5">
                        {selectedRole === 'student' && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            <input
                              type="text"
                              placeholder="First Name"
                              value={studentFirst}
                              onChange={(e) => setStudentFirst(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                              required
                            />
                            <input
                              type="text"
                              placeholder="Last Name"
                              value={studentLast}
                              onChange={(e) => setStudentLast(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                              required
                            />
                            <div className="space-y-0.5 text-left">
                              <span className="text-[8px] font-bold text-slate-500 uppercase tracking-widest pl-1">Birth Date</span>
                              <input
                                type="date"
                                value={studentDob}
                                onChange={(e) => setStudentDob(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white cursor-text"
                                required
                              />
                            </div>
                            <div className="space-y-0.5 text-left">
                              <span className="text-[8px] font-bold text-slate-500 uppercase tracking-widest pl-1">Class Year</span>
                              <input
                                type="text"
                                value={studentYear}
                                onChange={(e) => setStudentYear(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                                placeholder="e.g. 2026"
                                required
                              />
                            </div>
                          </div>
                        )}

                        {selectedRole === 'organization' && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            {orgJoinContext?.isJoinOrg && (
                              <div className="md:col-span-2 p-3 bg-sky-950/70 border border-sky-400/40 rounded-xl flex items-start gap-2.5 shadow-sm text-left animate-fade-in">
                                <div className="p-1 rounded bg-sky-500/20 text-sky-300 shrink-0 mt-0.5">
                                  <Building2 className="w-4 h-4" />
                                </div>
                                <div className="space-y-0.5">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-sky-200">
                                      Joining Existing Organization Team
                                    </span>
                                    <span className="text-[8px] px-1.5 py-0.5 bg-sky-400/20 text-sky-300 font-extrabold rounded flex items-center gap-1">
                                      <Lock className="w-2.5 h-2.5" /> Locked
                                    </span>
                                  </div>
                                  <p className="text-[11px] text-slate-200 font-medium leading-tight">
                                    You are joining <strong className="text-white">{employerOrg || orgJoinContext.orgName}</strong> in <strong className="text-white uppercase">{activeCountyConfig?.name || selectedCountyId}</strong>.
                                  </p>
                                  {orgJoinContext.extendedRegions && orgJoinContext.extendedRegions.length > 0 && (
                                    <p className="text-[9.5px] text-sky-300/90 font-medium">
                                      Inherited Secondary Regions: <span className="text-white font-bold">{orgJoinContext.extendedRegions.join(', ')}</span>
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                            <div className="md:col-span-2 relative">
                              <input
                                type="text"
                                placeholder="Enterprise Name"
                                value={employerOrg}
                                readOnly={!!orgJoinContext?.isJoinOrg}
                                onChange={(e) => setEmployerOrg(e.target.value)}
                                className={`bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white ${orgJoinContext?.isJoinOrg ? 'opacity-90 cursor-not-allowed pr-8 bg-sky-950/30 border-sky-400/30' : ''}`}
                                required
                              />
                              {orgJoinContext?.isJoinOrg && (
                                <Lock className="w-3.5 h-3.5 text-sky-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                              )}
                            </div>
                            <input
                              type="text"
                              placeholder="Contact First Name"
                              value={employerFirst}
                              onChange={(e) => setEmployerFirst(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                              required
                            />
                            <input
                              type="text"
                              placeholder="Contact Last Name"
                              value={employerLast}
                              onChange={(e) => setEmployerLast(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                              required
                            />
                            <div className="md:col-span-2">
                              <VerifiedAddressInput
                                id="map-employer-address"
                                value={employerAddress}
                                onChange={(val) => setEmployerAddress(val)}
                                placeholder="Registered Office Headquarters Address"
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                              />
                            </div>
                          </div>
                        )}

                        {selectedRole === 'advisor' && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            <input
                              type="text"
                              placeholder="First Name"
                              value={advisorFirst}
                              onChange={(e) => setAdvisorFirst(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                              required
                            />
                            <input
                              type="text"
                              placeholder="Last Name"
                              value={advisorLast}
                              onChange={(e) => setAdvisorLast(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                              required
                            />
                          </div>
                        )}

                        {selectedRole === 'parent' && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            <input
                              type="text"
                              placeholder="Parent First Name"
                              value={parentFirst}
                              onChange={(e) => setParentFirst(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                              required
                            />
                            <input
                              type="text"
                              placeholder="Parent Last Name"
                              value={parentLast}
                              onChange={(e) => setParentLast(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                              required
                            />
                            <input
                              type="text"
                              placeholder="Student Child's Full Name"
                              value={parentStudentName}
                              onChange={(e) => setParentStudentName(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white md:col-span-2"
                              required
                            />
                            <div className="md:col-span-2 grid grid-cols-2 gap-2.5">
                              <div className="space-y-1">
                                <span className="text-[8px] font-bold text-slate-500 uppercase tracking-widest pl-1">Child's Birth Date</span>
                                <input
                                  type="date"
                                  value={parentChildDob}
                                  onChange={(e) => setParentChildDob(e.target.value)}
                                  className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                                  required
                                />
                              </div>
                              <div className="space-y-1">
                                <span className="text-[8px] font-bold text-slate-500 uppercase tracking-widest pl-1">Student Access Code</span>
                                <input
                                  type="text"
                                  placeholder="e.g. A4NNGB"
                                  value={parentAccessCode}
                                  onChange={(e) => setParentAccessCode(e.target.value.toUpperCase())}
                                  className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white uppercase"
                                  required
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={handleNextStage}
                          className="w-full text-xs font-black uppercase tracking-wider text-slate-950 py-2.5 rounded-xl transition-all shadow-lg hover:brightness-110 active:scale-98 cursor-pointer flex items-center justify-center border-0 mt-1"
                          style={{ backgroundColor: dynamicBrandingTheme.primary }}
                        >
                          Continue &rarr;
                        </button>
                      </div>
                    )}

                    {/* STAGE 2: Credentials Detail (All Roles) */}
                    {signUpStage === 2 && (
                      <div className="space-y-2.5">
                        {selectedRole === 'student' && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            <div className="space-y-0.5 text-left md:col-span-2">
                              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest pl-1">1. Traditional High School (Required)</span>
                              <select
                                value={studentSchool}
                                onChange={(e) => setStudentSchool(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 text-xs outline-none w-full font-bold cursor-pointer text-white"
                                required
                              >
                                <option value="" className="bg-slate-900 text-slate-400">Select Traditional High School</option>
                                {getCountySchoolList(selectedCountyId).filter(s => isTraditionalSchool(s)).map((s, idx) => (
                                  <option key={idx} value={s} className="bg-slate-900 text-white">{s.replace(` (${selectedCountyId})`, '')}</option>
                                ))}
                              </select>
                            </div>

                            <div className="space-y-0.5 text-left md:col-span-2">
                              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest pl-1">2. Career & Tech Center (Optional)</span>
                              <select
                                value={studentCtc}
                                onChange={(e) => setStudentCtc(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 text-xs outline-none w-full font-bold cursor-pointer text-white"
                              >
                                <option value="" className="bg-slate-900 text-slate-400">None / Skip Career & Tech Center</option>
                                {getCountySchoolList(selectedCountyId).filter(s => isCareerTechCenter(s)).map((s, idx) => (
                                  <option key={idx} value={s} className="bg-slate-900 text-white">{s.replace(` (${selectedCountyId})`, '')}</option>
                                ))}
                              </select>
                            </div>
                            <input
                              type="email"
                              placeholder="Secure Student Email"
                              value={studentEmail}
                              onChange={(e) => setStudentEmail(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                              required
                            />
                            <div className="relative">
                              <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Secure Account Password" autoComplete="new-password"
                                value={studentPassword}
                                onChange={(e) => setStudentPassword(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-mono font-semibold text-white pr-10"
                                required
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                              >
                                {showPassword ? <EyeOff className="w-4 h-4 text-white" /> : <Eye className="w-4 h-4 text-white" />}
                              </button>
                            </div>
                            <div className="relative">
                              <input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm Password" autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-mono font-semibold text-white pr-10"
                                required
                              />
                              <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                              >
                                {showConfirmPassword ? <EyeOff className="w-4 h-4 text-white" /> : <Eye className="w-4 h-4 text-white" />}
                              </button>
                            </div>
                          </div>
                        )}

                        {selectedRole === 'organization' && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            <input
                              type="email"
                              placeholder="Business Contact Email"
                              value={employerEmail}
                              onChange={(e) => setEmployerEmail(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white md:col-span-2"
                              required
                            />
                            <div className="relative md:col-span-2">
                              <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Secure Administrator Password" autoComplete="new-password"
                                value={employerPassword}
                                onChange={(e) => setEmployerPassword(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-mono font-semibold text-white pr-10"
                                required
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-white hover:text-white"
                              >
                                {showPassword ? <EyeOff className="w-4 h-4 text-white" /> : <Eye className="w-4 h-4 text-white" />}
                              </button>
                            </div>
                            <div className="relative md:col-span-2">
                              <input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm Password" autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-mono font-semibold text-white pr-10"
                                required
                              />
                              <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-white hover:text-white"
                              >
                                {showConfirmPassword ? <EyeOff className="w-4 h-4 text-white" /> : <Eye className="w-4 h-4 text-white" />}
                              </button>
                            </div>
                          </div>
                        )}

                        {selectedRole === 'advisor' && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            <div className="space-y-0.5 text-left md:col-span-2">
                              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest pl-1">Advisor School Base</span>
                              <select
                                value={advisorSchool}
                                onChange={(e) => setAdvisorSchool(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 text-xs outline-none w-full font-bold cursor-pointer text-white"
                                required
                              >
                                <option value="" className="bg-slate-900 text-slate-400">Select School</option>
                                {getCountySchoolList(selectedCountyId).map((s, idx) => (
                                  <option key={idx} value={s} className="bg-slate-900 text-white">{s.replace(` (${selectedCountyId})`, '')}</option>
                                ))}
                              </select>
                            </div>
                            <input
                              type="email"
                              placeholder="Designated Academic Email"
                              value={advisorEmail}
                              onChange={(e) => setAdvisorEmail(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                              required
                            />
                            <div className="relative">
                              <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Counselor Access Key/Password" autoComplete="new-password"
                                value={advisorPassword}
                                onChange={(e) => setAdvisorPassword(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-mono font-semibold text-white pr-10"
                                required
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                              >
                                {showPassword ? <EyeOff className="w-4 h-4 text-white" /> : <Eye className="w-4 h-4 text-white" />}
                              </button>
                            </div>
                            <div className="relative">
                              <input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm Password" autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-mono font-semibold text-white pr-10"
                                required
                              />
                              <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                              >
                                {showConfirmPassword ? <EyeOff className="w-4 h-4 text-white" /> : <Eye className="w-4 h-4 text-white" />}
                              </button>
                            </div>
                          </div>
                        )}

                        {selectedRole === 'parent' && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            <div className="space-y-0.5 text-left md:col-span-2">
                              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest pl-1">Child's School Base Center</span>
                              <select
                                value={parentStudentSchool}
                                onChange={(e) => setParentStudentSchool(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 text-xs outline-none w-full font-bold cursor-pointer text-white"
                                required
                              >
                                <option value="" className="bg-slate-900 text-slate-400">Select School</option>
                                {getCountySchoolList(selectedCountyId).map((s, idx) => (
                                  <option key={idx} value={s} className="bg-slate-900 text-white">{s.replace(` (${selectedCountyId})`, '')}</option>
                                ))}
                              </select>
                            </div>
                            <input
                              type="email"
                              placeholder="Secure Parent Email"
                              value={parentEmail}
                              onChange={(e) => setParentEmail(e.target.value)}
                              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white"
                              required
                            />
                            <div className="relative">
                              <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Secure Account Password" autoComplete="new-password"
                                value={parentPassword}
                                onChange={(e) => setParentPassword(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-mono font-semibold text-white pr-10"
                                required
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                              >
                                {showPassword ? <EyeOff className="w-4 h-4 text-white" /> : <Eye className="w-4 h-4 text-white" />}
                              </button>
                            </div>
                            <div className="relative">
                              <input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm Password" autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-mono font-semibold text-white pr-10"
                                required
                              />
                              <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                              >
                                {showConfirmPassword ? <EyeOff className="w-4 h-4 text-white" /> : <Eye className="w-4 h-4 text-white" />}
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="bg-white/5 border border-white/10 rounded-lg p-2.5 mt-2 mb-4">
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px]">
                            <div className="flex items-center gap-1">
                              {hasMinLength ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 stroke-[3]" /> : <AlertCircle className="w-3.5 h-3.5 text-slate-400" />}
                              <span className={hasMinLength ? "text-emerald-500 font-bold" : "text-slate-400"}>8+ chars</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {hasCapital ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 stroke-[3]" /> : <AlertCircle className="w-3.5 h-3.5 text-slate-400" />}
                              <span className={hasCapital ? "text-emerald-500 font-bold" : "text-slate-400"}>1+ capital</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {hasNumber ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 stroke-[3]" /> : <AlertCircle className="w-3.5 h-3.5 text-slate-400" />}
                              <span className={hasNumber ? "text-emerald-500 font-bold" : "text-slate-400"}>1+ number</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {hasSymbol ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 stroke-[3]" /> : <AlertCircle className="w-3.5 h-3.5 text-slate-400" />}
                              <span className={hasSymbol ? "text-emerald-500 font-bold" : "text-slate-400"}>1+ symbol</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {passwordsMatch ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 stroke-[3]" /> : <AlertCircle className="w-3.5 h-3.5 text-slate-400" />}
                              <span className={passwordsMatch ? "text-emerald-500 font-bold" : "text-slate-400"}>Match</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2.5 mt-1.5">
                          <button
                            type="button"
                            onClick={() => { setSignUpStage(1); setAuthError(null); }}
                            className="w-1/3 text-xs font-black uppercase tracking-wider text-slate-300 bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 rounded-xl transition-all cursor-pointer text-center"
                          >
                            &larr; Back
                          </button>
                          {selectedRole === 'student' ? (
                            <button
                              type="button"
                              onClick={handleNextStage}
                              className="w-2/3 text-xs font-black uppercase tracking-wider text-slate-950 py-2.5 rounded-xl transition-all shadow-lg hover:brightness-110 active:scale-98 cursor-pointer flex items-center justify-center border-0 font-extrabold"
                              style={{ backgroundColor: dynamicBrandingTheme.primary }}
                            >
                              Continue &rarr;
                            </button>
                          ) : (
                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className="w-2/3 text-xs font-black uppercase tracking-wider text-slate-950 py-2.5 rounded-xl transition-all shadow-lg hover:brightness-110 active:scale-98 cursor-pointer flex items-center justify-center border-0 font-extrabold"
                              style={{ backgroundColor: dynamicBrandingTheme.primary }}
                            >
                              {isSubmitting ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin text-slate-950" />
                                  <span>Registering...</span>
                                </>
                              ) : (
                                <span>Setup Credentials</span>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* STAGE 3: Parent Contact (Students Only) */}
                    {signUpStage === 3 && selectedRole === 'student' && (() => {
                      const birthDate = new Date(studentDob);
                      const today = new Date();
                      let age = today.getFullYear() - birthDate.getFullYear();
                      const m = today.getMonth() - birthDate.getMonth();
                      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) { age--; }
                      const isUnder18 = age < 18;
                      
                      return (
                        <div className="space-y-3.5 animate-fade-in">
                          {isUnder18 ? (
                            <div className="space-y-1 text-left">
                              <div className="flex justify-between items-center pb-1">
                                <span className="text-[9px] font-extrabold text-slate-400 uppercase tracking-widest pl-1">
                                  Parent or Guardian Email Address
                                </span>
                                <span className="text-[8px] px-1.5 py-0.5 bg-red-500/20 text-red-400 font-extrabold rounded uppercase tracking-wider">Required (Under 18)</span>
                              </div>
                              <input
                                type="email"
                                placeholder="parent.guardian@example.com"
                                value={studentParentEmail}
                                onChange={(e) => setStudentParentEmail(e.target.value)}
                                className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white placeholder-slate-500"
                                required
                              />
                            </div>
                          ) : (
                            <div className="space-y-3 text-left">
                              <div className="flex justify-between items-center pb-1">
                                <span className="text-[9px] font-extrabold text-slate-400 uppercase tracking-widest pl-1">
                                  Parent Email Contact
                                </span>
                                <span className="text-[8px] px-1.5 py-0.5 bg-white/10 text-slate-300 font-extrabold rounded uppercase tracking-wider">Optional (18+)</span>
                              </div>

                              <p className="text-[10px] text-slate-300 leading-normal pl-1">
                                Would you like to provide a parent or guardian email address to receive career placement alerts and work authorization updates?
                              </p>

                              <div className="flex rounded-xl bg-white/5 p-1 border border-white/10">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setProvideParentEmailMap(true);
                                  }}
                                  className={`flex-1 py-1.5 text-[9px] font-black uppercase tracking-widest rounded-lg transition-all flex items-center justify-center gap-1 cursor-pointer ${
                                    provideParentEmailMap ? '' : 'text-slate-400 hover:text-white'
                                  }`}
                                  style={provideParentEmailMap ? { backgroundColor: dynamicBrandingTheme.primary, color: '#090d16' } : {}}
                                >
                                  Yes, Provide Email
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setProvideParentEmailMap(false);
                                    setStudentParentEmail('');
                                  }}
                                  className={`flex-1 py-1.5 text-[9px] font-black uppercase tracking-widest rounded-lg transition-all flex items-center justify-center gap-1 cursor-pointer ${
                                    !provideParentEmailMap ? '' : 'text-slate-400 hover:text-white'
                                  }`}
                                  style={!provideParentEmailMap ? { backgroundColor: dynamicBrandingTheme.primary, color: '#090d16' } : {}}
                                >
                                  No, Skip
                                </button>
                              </div>

                              {provideParentEmailMap && (
                                <div className="space-y-1 mt-2 animate-fade-in">
                                  <span className="text-[9px] font-extrabold text-slate-400 uppercase tracking-widest pl-1">
                                    Parent or Guardian Email Address
                                  </span>
                                  <input
                                    type="email"
                                    placeholder="parent.guardian@example.com"
                                    value={studentParentEmail}
                                    onChange={(e) => setStudentParentEmail(e.target.value)}
                                    className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs focus:bg-slate-900 focus:border-white outline-none w-full font-semibold text-white placeholder-slate-500"
                                  />
                                </div>
                              )}
                            </div>
                          )}

                          {/* Distinct Explanation Box to Avoid Confusion */}
                          <div className="bg-[#1E293B] border border-white/10 rounded-xl p-3 text-left space-y-1.5 shadow-sm">
                            <span className="text-[9px] font-black text-[#F9C74F] uppercase tracking-wider flex items-center gap-1.5">
                              <Info className="w-3.5 h-3.5 text-[#F9C74F] shrink-0" /> EXPLAINER: STUDENT vs. PARENT EMAIL
                            </span>
                            <p className="text-[10px] text-slate-300 font-medium leading-relaxed">
                              This <strong className="text-[#F9C74F]">must be your parent or guardian's email address</strong>, not your student email. 
                            </p>
                            <p className="text-[10px] text-slate-300 font-medium leading-relaxed">
                              Under educational guidelines (FERPA consent rules), we are required to obtain verifiable parental authorization for students under 18 before arranging career opportunities and work-based placements. Your parent will receive a secure portal link to grant consent.
                            </p>
                          </div>

                          <div className="flex gap-2.5 mt-2">
                            <button
                              type="button"
                              onClick={() => { setSignUpStage(2); setAuthError(null); }}
                              className="w-1/3 text-xs font-black uppercase tracking-wider text-slate-300 bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 rounded-xl transition-all cursor-pointer text-center"
                            >
                              &larr; Back
                            </button>
                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className="w-2/3 text-xs font-black uppercase tracking-wider text-slate-950 py-2.5 rounded-xl transition-all shadow-lg hover:brightness-110 active:scale-98 cursor-pointer flex items-center justify-center border-0 font-extrabold"
                              style={{ backgroundColor: dynamicBrandingTheme.primary }}
                            >
                              {isSubmitting ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin text-slate-950" />
                                  <span>Registering...</span>
                                </>
                              ) : (
                                <span>Register Network Profile</span>
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </form>
              ) : (
                <form onSubmit={handleLocalSignIn} className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-0.5">
                    <div className="space-y-1 text-left">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Workspace Email</label>
                      <input
                        type="email"
                        placeholder="yourname@domain.com"
                        value={signinEmail}
                        onChange={(e) => setSigninEmail(e.target.value)}
                        className="bg-white/5 border border-white/10 hover:border-white/20 focus:border-white rounded-xl px-3 py-2 text-xs w-full outline-none font-semibold text-white transition-all focus:bg-slate-900"
                        required
                      />
                    </div>
                    
                    <div className="space-y-1 text-left">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Secure Access Key</label>
                      <input
                        type="password"
                        placeholder="••••••••••••"
                        value={signinPassword}
                        onChange={(e) => setSigninPassword(e.target.value)}
                        className="bg-white/5 border border-white/10 hover:border-white/20 focus:border-white rounded-xl px-3 py-2 text-xs w-full outline-none font-mono font-semibold text-white transition-all focus:bg-slate-900"
                        required
                      />
                      <p className="text-[9px] text-slate-500 mt-1 pl-1">
                        Registered demo profiles can quickly log in via presets on the main dashboard sidebar.
                      </p>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full text-xs font-black uppercase tracking-wider text-slate-950 py-2.5 rounded-xl transition-all shadow-lg hover:scale-[1.01] active:scale-98 cursor-pointer flex items-center justify-center border-0 mt-3"
                    style={{ backgroundColor: dynamicBrandingTheme.primary }}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin text-slate-950" />
                        <span>Unlocking Terminal...</span>
                      </>
                    ) : (
                      <>
                        <LogIn className="w-4 h-4 mr-2 text-slate-950" />
                        <span>Unlock Workspace Portal</span>
                      </>
                    )}
                  </button>
                </form>
              )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ) : selectedInactiveCounty ? (
          
          /* 3. INACTIVE REGION ACTIVATION REQUEST VIEW */
          <motion.div 
            key="inactive-portal-view"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 180, damping: 25 }}
            className="w-full max-w-[650px] bg-slate-950/90 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl flex flex-col items-stretch overflow-hidden isolate transform-gpu text-white"
          >
            {/* TWO CELL GRID */}
            <div className="flex flex-col md:flex-row items-stretch border-b border-white/10">
              
              {/* CELL 1: REGION MAP DISPLAY */}
              <div className="w-full md:w-1/2 bg-slate-900/60 p-3.5 md:p-4 flex flex-col justify-between items-center gap-3 relative border-b md:border-b-0 md:border-r border-white/10 rounded-t-2xl md:rounded-tr-none md:rounded-l-2xl md:rounded-bl-2xl">
                {/* Visual tech-line grids for aesthetic modern feel */}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />

                <div className="w-full flex items-center justify-start z-10">
                  <button 
                    onClick={handleBackToMap}
                    className="text-slate-300 hover:text-white text-[9px] md:text-[10.5px] tracking-wider uppercase font-extrabold flex items-center gap-1.5 bg-white/5 hover:bg-white/10 px-2.5 py-1.5 rounded-md border border-white/10 transition-all shadow-md cursor-pointer"
                  >
                    <ArrowLeft className="w-2.5 h-2.5" /> Back to Map
                  </button>
                </div>

                {/* Glowing isolated shape display */}
                <div className="flex-1 w-full flex flex-col items-center justify-center p-3 relative min-h-[150px] md:min-h-[170px]">
                  <div 
                    className="absolute inset-4 blur-2xl opacity-25 transition-all duration-700 pointer-events-none" 
                    style={{ backgroundColor: '#0094c8', boxShadow: '0 0 50px 15px rgba(0, 148, 200, 0.4)' }}
                  />
                  <div className="w-full h-full flex items-center justify-center relative">
                    <svg
                      viewBox={getCountyBBox(selectedInactiveCounty.id) || "0 0 100 100"}
                      preserveAspectRatio="xMidYMid meet"
                      className="w-full h-full max-h-[135px] sm:max-h-[150px] md:max-h-[165px] relative drop-shadow-[0_0_15px_rgba(56,189,248,0.25)] select-none animate-pulse duration-[4000ms]"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <motion.g layoutId={`county-g-${selectedInactiveCounty.id}`}>
                        {selectedInactiveCounty.paths.map((d, index) => (
                          <motion.path
                            key={`${selectedInactiveCounty.id}-${index}`}
                            layoutId={`county-path-${selectedInactiveCounty.id}-${index}`}
                            d={d}
                            fill="rgba(56, 189, 248, 0.12)"
                            stroke="#38bdf8"
                            strokeWidth="2"
                            strokeLinejoin="round"
                            vectorEffect="non-scaling-stroke"
                            style={{
                              filter: 'drop-shadow(0 0 8px rgba(56, 189, 248, 0.8))'
                            }}
                          />
                        ))}
                      </motion.g>
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                      <div className="text-[10px] sm:text-[11px] md:text-[12px] font-black uppercase tracking-widest text-white select-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] bg-slate-950/80 px-3 py-1 rounded-full border border-sky-400/30 backdrop-blur-md">
                        {selectedInactiveCounty.name}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status & expansion banner */}
                <div className="w-full z-10 flex flex-col items-center gap-1 text-center">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider bg-sky-500/10 text-sky-300 border border-sky-500/25">
                    <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-ping" />
                    Region Not Active • Regional Inquiry
                  </span>
                  <p className="text-[10px] text-slate-400 leading-tight max-w-[220px] mt-1">
                    Interested in bringing Work-Based Learning to schools, students, and employers in {selectedInactiveCounty.name} County? Send us an inquiry to learn more.
                  </p>
                </div>
              </div>

              {/* CELL 2: INACTIVE REGION REQUEST FORM */}
              <div className="w-full md:w-1/2 p-4 md:p-5 flex flex-col justify-between relative bg-slate-950/40">
                {inquirySuccess ? (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center justify-center text-center py-6 space-y-3 my-auto"
                  >
                    <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                      <CheckCircle2 className="w-6 h-6" />
                    </div>
                    <h4 className="text-sm font-black text-white uppercase tracking-wider">
                      Inquiry Sent!
                    </h4>
                    <p className="text-xs text-slate-300 leading-relaxed max-w-xs">
                      Thank you, <span className="text-sky-300 font-bold">{inquiryName}</span>! Your inquiry regarding Work-Based Learning in <span className="text-white font-bold">{selectedInactiveCounty.name} County</span> has been received.
                    </p>
                    <p className="text-[10px] text-slate-400 leading-relaxed max-w-xs">
                      Our team will review your message and reach out with information on regional setup, district partnerships, and next steps.
                    </p>
                    <button
                      type="button"
                      onClick={handleBackToMap}
                      className="mt-3 px-5 py-2 bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all cursor-pointer shadow-md"
                    >
                      Return to Map
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleInquirySubmit} className="flex flex-col gap-3 text-left my-auto">
                    <div>
                      <div className="flex items-center gap-1.5 text-sky-400 text-[10px] font-bold uppercase tracking-wider">
                        <Info className="w-3 h-3" />
                        <span>Regional Inquiry</span>
                      </div>
                      <h3 className="text-base font-black text-white tracking-tight mt-0.5">
                        Inquire About {selectedInactiveCounty.name} County
                      </h3>
                      <p className="text-[11px] text-slate-400 leading-snug mt-1">
                        Send us your contact details to learn more about bringing Work-Based Learning to your area.
                      </p>
                    </div>

                    {inquiryError && (
                      <div className="flex items-center gap-2 p-2 bg-rose-500/10 border border-rose-500/25 rounded-lg text-rose-300 text-[10px]">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                        <span>{inquiryError}</span>
                      </div>
                    )}

                    {/* Streamlined Form Fields */}
                    <div className="space-y-2.5">
                      <div>
                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={inquiryName}
                          onChange={(e) => setInquiryName(e.target.value)}
                          placeholder="Full Name"
                          className="w-full bg-white/5 border border-white/10 focus:border-sky-400 rounded-lg px-3 py-2 text-xs text-white outline-none transition-colors"
                        />
                      </div>
                      <div>
                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          required
                          value={inquiryEmail}
                          onChange={(e) => setInquiryEmail(e.target.value)}
                          placeholder="name@organization.com"
                          className="w-full bg-white/5 border border-white/10 focus:border-sky-400 rounded-lg px-3 py-2 text-xs text-white outline-none transition-colors"
                        />
                      </div>
                      <div>
                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                          Organization / School <span className="text-slate-500 lowercase font-normal">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={inquiryOrg}
                          onChange={(e) => setInquiryOrg(e.target.value)}
                          placeholder="District, School, or Business"
                          className="w-full bg-white/5 border border-white/10 focus:border-sky-400 rounded-lg px-3 py-2 text-xs text-white outline-none transition-colors"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isInquirySubmitting}
                      className="w-full text-xs font-black uppercase tracking-wider text-slate-950 py-2.5 rounded-xl transition-all shadow-lg hover:bg-sky-300 bg-sky-400 active:scale-98 cursor-pointer flex items-center justify-center border-0 mt-1 disabled:opacity-50"
                    >
                      {isInquirySubmitting ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin text-slate-950" />
                          <span>Sending Inquiry...</span>
                        </>
                      ) : (
                        <>
                          <Info className="w-3.5 h-3.5 mr-1.5 text-slate-950" />
                          <span>Send Inquiry</span>
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};
